import React, {useEffect, useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {toast} from "react-toastify";
import {BeatLoader} from "react-spinners";
import {placeholderAPI, dbConnectionAPI, initializeiFExAuth} from "../../../Api/ifex";
import {FiX, FiCheck} from "react-icons/fi";
import Select from 'react-select';

// Custom styles for react-select to match Tailwind design
const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
        boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
        '&:hover': {
            borderColor: '#3b82f6'
        },
        minHeight: '42px',
        backgroundColor: state.isDisabled ? '#f3f4f6' : 'white',
        cursor: state.isDisabled ? 'not-allowed' : 'default'
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#dbeafe' : 'white',
        color: state.isSelected ? 'white' : '#1f2937',
        cursor: 'pointer',
        textAlign: 'left',
        '&:active': {
            backgroundColor: '#3b82f6'
        }
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999
    }),
    placeholder: (base) => ({
        ...base,
        color: '#9ca3af',
        textAlign: 'left'
    }),
    singleValue: (base, state) => ({
        ...base,
        color: state.isDisabled ? '#6b7280' : '#1f2937',
        textAlign: 'left'
    })
};

const PlaceholderCreateModal = ({visible, onClose, onSuccess, user}) => {
    const [saving, setSaving] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [validating, setValidating] = useState(false);
    const [nameAvailable, setNameAvailable] = useState(null);
    const [connections, setConnections] = useState([]);
    const [tables, setTables] = useState([]);
    const [fields, setFields] = useState([]);
    const [fieldPreview, setFieldPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);

    const {register, handleSubmit, watch, setValue, control, formState: {errors, isValid}} = useForm({
        mode: "onChange",
        defaultValues: {
            placeholder_name: '',
            is_active: '1',
            database_connection: '',
            schema: 'dbo',
            table_name: '',
            field_name: '',
            field_format: '',
            placeholder_type: 'standard',
            text_chain_formula: '',
            remarks: '',
        }
    });

    const watchedName = watch('placeholder_name');
    const watchedType = watch('placeholder_type');
    const watchedConnection = watch('database_connection');
    const watchedSchema = watch('schema');
    const watchedTable = watch('table_name');
    const watchedField = watch('field_name');

    useEffect(() => {
        if (visible) {
            setInitialLoading(true);
            fetchConnections();
        }
    }, [visible]);


    useEffect(() => {
        if (watchedConnection && watchedSchema) {
            fetchTables(watchedConnection, watchedSchema);
        }
    }, [watchedConnection, watchedSchema]);

    useEffect(() => {
        if (watchedTable && watchedConnection && watchedSchema) {
            fetchFields(watchedConnection, watchedTable, watchedSchema);
        }
    }, [watchedTable, watchedSchema]);

    // Fetch field preview when field is selected
    useEffect(() => {
        if (watchedField && watchedConnection && watchedTable && watchedSchema) {
            fetchFieldPreview(watchedConnection, watchedTable, watchedField, watchedSchema);
        } else {
            setFieldPreview(null);
        }
    }, [watchedField]);

    // Auto-generate placeholder name from field
    useEffect(() => {
        if (watchedField && !watchedName) {
            // Suggest placeholder name based on field
            const suggested = `[M_${watchedField}]`;
            setValue('placeholder_name', suggested);
        }
    }, [watchedField]);

    // Validate placeholder name
    useEffect(() => {
        const timer = setTimeout(() => {
            if (watchedName && watchedName.match(/^\[([A-Z]+_[A-Za-z0-9]+)\]$/)) {
                validatePlaceholderName(watchedName);
            } else {
                setNameAvailable(null);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [watchedName]);

    const fetchConnections = async () => {
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                setInitialLoading(false);
                return;
            }

            const response = await dbConnectionAPI.getAll();
            if (response.data.success) {
                setConnections(response.data.data || []);
            }
            // Always hide loading after API call completes
            setInitialLoading(false);
        } catch (error) {
            console.error('Error fetching connections:', error);
            toast.error('Fehler beim Laden der Verbindungen');
            setInitialLoading(false); // Show form even if connections fail
        }
    };

    const fetchTables = async (connectionId, schema = 'dbo') => {
        setTables([]);
        setFields([]);
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                return;
            }

            const response = await dbConnectionAPI.getTables(connectionId, schema);
            if (response.data.success) {
                // Remove duplicates and sort tables alphabetically
                const uniqueTables = Array.from(new Set(response.data.tables || []));
                const sortedTables = uniqueTables.sort((a, b) =>
                    a.localeCompare(b, undefined, {sensitivity: 'base'})
                );
                setTables(sortedTables);
            }
        } catch (error) {
            console.error('Error fetching tables:', error);
            toast.error('Fehler beim Laden der Tabellen');
        }
    };

    const fetchFields = async (connectionId, tableName, schema = 'dbo') => {
        setFields([]);
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                return;
            }

            const response = await dbConnectionAPI.getTableFields(connectionId, tableName, schema);
            if (response.data.success) {
                // Remove duplicates based on field name and sort alphabetically
                const fieldsArray = response.data.fields || [];
                const uniqueFields = fieldsArray.filter((field, index, self) => {
                    const fieldName = field.name || field;
                    return index === self.findIndex(f => (f.name || f) === fieldName);
                });
                const sortedFields = uniqueFields.sort((a, b) => {
                    const nameA = a.name || a;
                    const nameB = b.name || b;
                    return nameA.localeCompare(nameB, undefined, {sensitivity: 'base'});
                });
                setFields(sortedFields);
            }
        } catch (error) {
            console.error('Error fetching fields:', error);
            toast.error('Fehler beim Laden der Felder');
        }
    };

    const fetchFieldPreview = async (connectionId, tableName, fieldName, schema = 'dbo') => {
        setLoadingPreview(true);
        setFieldPreview(null);
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                return;
            }

            const response = await dbConnectionAPI.getFieldPreview(
                connectionId,
                tableName,
                fieldName,
                schema,
                5
            );

            if (response.data.success) {
                setFieldPreview(response.data.samples);
            }
        } catch (error) {
            console.error('Error fetching field preview:', error);
            // Don't show error toast for preview failures (non-critical)
        } finally {
            setLoadingPreview(false);
        }
    };

    const validatePlaceholderName = async (name) => {
        setValidating(true);
        try {
            // Remove brackets for validation
            const cleanName = name.replace(/[\[\]]/g, '');
            const response = await placeholderAPI.validateName(cleanName);
            setNameAvailable(response.data.available);
        } catch (error) {
            console.error('Error validating name:', error);
            setNameAvailable(null);
        } finally {
            setValidating(false);
        }
    };

    const onSubmit = async (data) => {
        if (nameAvailable === false) {
            toast.error('Platzhalter-Name ist bereits vergeben');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...data,
                is_active: data.is_active === '1',
                created_by: user.name || user.email,
            };

            await placeholderAPI.create(payload);
            toast.success('Platzhalter erfolgreich erstellt');
            onSuccess();
        } catch (error) {
            console.error('Error creating placeholder:', error);
            if (error.response?.data?.errors) {
                Object.values(error.response.data.errors).forEach(err => {
                    toast.error(err[0]);
                });
            } else {
                toast.error('Fehler beim Erstellen des Platzhalters');
            }
        } finally {
            setSaving(false);
        }
    };

    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-modalBackDropColor bg-opacity-60" onClick={onClose}></div>
            <div className="relative z-10 bg-white rounded-xl shadow-2xl w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-borderBlue p-6 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-text">Neuen Platzhalter erstellen</h2>
                    <button onClick={onClose} className="text-grey hover:text-text transition">
                        <FiX size={24} />
                    </button>
                </div>

                {/* Loading State */}
                {initialLoading && (
                    <div className="p-6 flex items-center justify-center min-h-[400px]">
                        <div className="text-center">
                            <BeatLoader size={15} color="#3A46A9" />
                            <p className="text-grey mt-4">Lade Daten...</p>
                        </div>
                    </div>
                )}

                {/* Form */}
                {!initialLoading && (
                <form onSubmit={handleSubmit(onSubmit)} className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Placeholder Name */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-text mb-2">
                                Platzhalter-Name *
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    className={`w-full px-3 py-2 border rounded text-text focus:outline-none ${errors.placeholder_name ? 'border-cancel' : 'border-borderBlue focus:border-mainBlue'}`}
                                    placeholder="[M_Vorname]"
                                    {...register('placeholder_name', {
                                        required: 'Platzhalter-Name ist erforderlich',
                                        pattern: {
                                            value: /^\[([A-Z]+_[A-Za-z0-9]+)\]$/,
                                            message: 'Format: [PRÄFIX_Name] (z.B. [M_Vorname])'
                                        }
                                    })}
                                />
                                {validating && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <BeatLoader size={8} color="#3A46A9" />
                                    </div>
                                )}
                                {!validating && nameAvailable === true && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green">
                                        <FiCheck size={20} />
                                    </div>
                                )}
                                {!validating && nameAvailable === false && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-cancel">
                                        <FiX size={20} />
                                    </div>
                                )}
                            </div>
                            {errors.placeholder_name && <p className="text-cancel text-sm mt-1">{errors.placeholder_name.message}</p>}
                            {nameAvailable === false && <p className="text-cancel text-sm mt-1">Dieser Name ist bereits vergeben</p>}
                            <p className="text-xs text-grey mt-1">
                                Präfixe: M_ (Mitarbeiter), F_ (Firma), A_ (Arbeitgeber), P_ (Partner)
                            </p>
                        </div>

                        {/* Active Status */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">Status *</label>
                            <select className="w-full px-3 py-2 border border-borderBlue rounded text-text focus:border-mainBlue focus:outline-none" {...register('is_active')}>
                                <option value="1">Aktiv</option>
                                <option value="0">Inaktiv</option>
                            </select>
                        </div>

                        {/* Placeholder Type */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">Typ *</label>
                            <select className="w-full px-3 py-2 border border-borderBlue rounded text-text focus:border-mainBlue focus:outline-none" {...register('placeholder_type')}>
                                <option value="standard">Standard</option>
                                <option value="required">Pflichtfeld</option>
                                <option value="formfield">Formularfeld</option>
                                <option value="textchain">Textkette</option>
                            </select>
                        </div>

                        {/* Database Connection */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">Datenbankverbindung</label>
                            <select className="w-full px-3 py-2 border border-borderBlue rounded text-text focus:border-mainBlue focus:outline-none" {...register('database_connection')}>
                                <option value="">-- Wählen --</option>
                                {connections.map(conn => (
                                    <option key={conn.id} value={conn.id}>{conn.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Schema Selection */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">
                                Schema <span className="text-cancel">*</span>
                            </label>
                            <select
                                className="w-full px-3 py-2 border border-borderBlue rounded text-text focus:border-mainBlue focus:outline-none"
                                {...register('schema', {required: 'Schema ist erforderlich'})}
                                disabled={!watchedConnection}
                            >
                                <option value="dbo">dbo</option>
                                <option value="dgg">dgg</option>
                            </select>
                            {errors.schema && <p className="text-cancel text-sm mt-1">{errors.schema.message}</p>}
                        </div>

                        {/* Table Name */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">Tabelle</label>
                            <Controller
                                name="table_name"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={tables.map(table => ({ value: table, label: table }))}
                                        value={field.value ? { value: field.value, label: field.value } : null}
                                        onChange={(option) => field.onChange(option?.value || '')}
                                        styles={selectStyles}
                                        isDisabled={!watchedConnection}
                                        placeholder="-- Wählen --"
                                        isClearable
                                        isSearchable
                                        noOptionsMessage={() => "Keine Tabellen gefunden"}
                                    />
                                )}
                            />
                        </div>

                        {/* Field Name */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">Feld</label>
                            <Controller
                                name="field_name"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={fields.map(f => {
                                            const fieldName = f.name || f;
                                            const fieldType = f.type || '';
                                            const maxLength = f.maxLength ? `(${f.maxLength})` : '';
                                            const nullable = f.nullable === false ? ' *' : '';

                                            return {
                                                value: fieldName,
                                                label: `${fieldName} ${fieldType}${maxLength}${nullable}`.trim()
                                            };
                                        })}
                                        value={field.value ? (() => {
                                            const selectedField = fields.find(f => (f.name || f) === field.value);
                                            if (selectedField) {
                                                const fieldType = selectedField.type || '';
                                                const maxLength = selectedField.maxLength ? `(${selectedField.maxLength})` : '';
                                                const nullable = selectedField.nullable === false ? ' *' : '';
                                                return {
                                                    value: field.value,
                                                    label: `${field.value} ${fieldType}${maxLength}${nullable}`.trim()
                                                };
                                            }
                                            return { value: field.value, label: field.value };
                                        })() : null}
                                        onChange={(option) => field.onChange(option?.value || '')}
                                        styles={selectStyles}
                                        isDisabled={!watchedTable}
                                        placeholder="-- Wählen --"
                                        isClearable
                                        isSearchable
                                        noOptionsMessage={() => "Keine Felder gefunden"}
                                    />
                                )}
                            />

                            {/* Field Preview Panel */}
                            {watchedField && (
                                <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded">
                                    <p className="text-xs font-semibold text-gray-700 mb-1">Beispielwerte:</p>
                                    {loadingPreview ? (
                                        <p className="text-xs text-gray-500">Lade Vorschau...</p>
                                    ) : fieldPreview && fieldPreview.length > 0 ? (
                                        <ul className="text-xs text-gray-600 space-y-0.5">
                                            {fieldPreview.map((sample, idx) => (
                                                <li key={idx} className="font-mono">• "{sample}"</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-xs text-gray-500">Keine Beispielwerte verfügbar</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Field Format */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">Feldformat</label>
                            <select className="w-full px-3 py-2 border border-borderBlue rounded text-text focus:border-mainBlue focus:outline-none" {...register('field_format')}>
                                <option value="">-- Wählen --</option>
                                <option value="text">Text</option>
                                <option value="date_de">Datum (TT.MM.JJJJ)</option>
                                <option value="plz">PLZ (00000)</option>
                                <option value="number">Zahl</option>
                                <option value="currency">Währung</option>
                            </select>
                        </div>

                        {/* Text Chain Formula */}
                        {watchedType === 'textchain' && (
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-text mb-2">Textketten-Formel</label>
                                <textarea
                                    className="w-full px-3 py-2 border border-borderBlue rounded text-text focus:border-mainBlue focus:outline-none"
                                    rows="3"
                                    placeholder="z.B. [M_Vorname] + ' ' + [M_Nachname]"
                                    {...register('text_chain_formula')}
                                />
                                <p className="text-xs text-grey mt-1">
                                    Verwenden Sie andere Platzhalter mit + für Verkettung
                                </p>
                            </div>
                        )}

                        {/* Remarks */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-text mb-2">Bemerkungen</label>
                            <textarea className="w-full px-3 py-2 border border-borderBlue rounded text-text focus:border-mainBlue focus:outline-none" rows="3" {...register('remarks')} />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-borderBlue">
                        <button type="button" onClick={onClose} className="px-6 py-2 bg-whiteDark hover:bg-silver text-text rounded transition">
                            Abbrechen
                        </button>
                        <button
                            type="submit"
                            disabled={saving || !isValid || nameAvailable === false}
                            className={`px-6 py-2 rounded text-white flex items-center gap-2 transition ${
                                saving || !isValid || nameAvailable === false ? 'bg-silver text-grey cursor-not-allowed' : 'bg-mainBlue hover:bg-opacity-90'
                            }`}
                        >
                            {saving && <BeatLoader size={8} color="#ffffff" />}
                            {saving ? 'Erstelle...' : 'Erstellen'}
                        </button>
                    </div>
                </form>
                )}
            </div>
        </div>
    );
};

export default PlaceholderCreateModal;
